import searchInsights from 'search-insights';
import { Cookies } from '../cookies';
import { algoliaAppId, algoliaInsightsApiKey } from './config';

// A consuming page can still override per-page via these globals; otherwise fall back to the
// build-time MIX_ALGOLIA_* configuration.
const trackingAppId = window.ALGOLIA_TRACKING_APP_ID || algoliaAppId;
const trackingApiKey = window.ALGOLIA_TRACKING_API_KEY || algoliaInsightsApiKey;

function isValidAlgoliaQueryID(queryID) {
    if (typeof queryID !== 'string') {
        console.error('queryID is not a string');
        return false;
    }

    const normalizedQueryID = queryID.trim();
    if (!normalizedQueryID) {
        console.error('normalizedQueryID is empty');
        return false;
    }

    if (/^[a-f0-9]{32}$/i.test(normalizedQueryID)) {
        return true;
    } else {
        console.error('normalizedQueryID is not a valid queryID');
    }

    return normalizedQueryID.startsWith('message_');
}

let cookies = new Cookies();

function getAlgoliaUserToken() {
    const token = cookies.get('_ALGOLIA');
    return typeof token === 'string' && token.length > 0 ? token : undefined;
}

function aa(...input) {
    const calls = JSON.parse(sessionStorage.getItem('_aaCall') || '[]');
    calls.push(input);
    sessionStorage.setItem('_aaCall', JSON.stringify(calls));

    const [method, payload] = input;
    if (payload?.queryID && !isValidAlgoliaQueryID(payload.queryID)) {
        console.warn(`Skipping Algolia "${method}" event: invalid queryID "${payload.queryID}"`);
        return;
    }

    searchInsights(...input);
}

function getCurrentQueryParams() {
    return new URLSearchParams(window.location.search);
}

function getDefaultIndex() {
    return window.location.hostname === 'www.sweetwater.com' ? 'production_products' : 'development_products';
}

function getQueryDataFromUrl() {
    const queryParams = getCurrentQueryParams();
    const queryID = queryParams.get('_queryID') || queryParams.get('_queryid');
    const index = queryParams.get('_index') || getDefaultIndex();

    return {
        queryID,
        index,
    };
}

window._aaInitialized = false;
function initialize() {
    if (!window._aaInitialized) {
        window._aaInitialized = true;
        const args = {
            apiKey: trackingApiKey,
            appId: trackingAppId,
            useCookie: cookies.userHasConsentedTo('performance'),
        };
        
        aa('init', args);
    }
}

export function sendAlgoliaClickEvent({ objectID, position = 1 }) {
    const dedupeKey = 'last_stored_click';
    if(sessionStorage.getItem(dedupeKey) === objectID) {
        return;
    }
    sessionStorage.setItem(dedupeKey, objectID);

    const { queryID, index } = getLatestAlgoliaQueryData();
    let eventMethod = 'clickedObjectIDsAfterSearch';
    if (!(queryID && index)) {
        eventMethod = 'clickedObjectIDs';
    }

    if (!queryID || !isValidAlgoliaQueryID(queryID)) {
        return;
    }

    initialize();

    const payload = {
        eventName: 'Product Clicked',
        index,
        queryID,
        objectIDs: [objectID],
        positions: [position],
    };
    const userToken = getAlgoliaUserToken();
    if (userToken) {
        payload.userToken = userToken;
    }

    aa(eventMethod, payload);
   
}

sendAlgoliaClickEvent.whenProductDetailPageLinkClicked = () => {
    const listenForDetailPageClicks = async () => {
        const productDetailLinks = await new Promise((resolve) => {
            let ticks = 0;
            const interval = setInterval(() => {
                const items = Array.from(document.querySelectorAll('[data-itemid]'));
                if (items.length > 0) {
                    resolve(items);
                    clearInterval(interval);
                }
                ticks++;
                if (ticks > 10) {
                    resolve([]);
                    clearInterval(interval);
                }
            }, 100);
        });

        // 🧙 Are you ready for a story? 📕
        // This block of code below is intended to cache the amount of
        // products per page for this search.
        // We have to do this in order to calculate the position of each
        // item in relation to the first product returned on page 1. (see the next chunk of comments below)
        // However, there is no javascript variable available to let us know how many
        // products are in each page.
        // So... If we're on page 1, we cache the amount of products that are on the page in 
        // session storage to reference on the next page.
        // If we're not on page 1 (and there isn't a page number cached in the session),
        // we just guess based on the amount of products in the page
        // (which is less accurate, but better than nothing)
        const pageNumber = parseInt(getCurrentQueryParams().get('pn') || getCurrentQueryParams().get('page') || 1);
        const prodPerPageCacheKey = window.location.href + ':products_per_page';
        let productsPerPage = parseInt(sessionStorage.getItem(prodPerPageCacheKey));
        if (!Number.isInteger(productsPerPage)) {
            productsPerPage = productDetailLinks.length;
            if (pageNumber == 1 && productsPerPage > 0) {
                sessionStorage.setItem(prodPerPageCacheKey, productsPerPage);
            }
        }
        
        productDetailLinks.forEach((itemLink, _position) => {
            if (!itemLink.dataset.itemid) {
                return;
            }
            const objectID = itemLink.dataset.itemid;
            const gridindex = _position + 1;
            const position = gridindex + (pageNumber > 1 ? ((pageNumber - 1) * productsPerPage) : 0);
            
            // About that... 👆 Let me explain myself. 👇
            // Algolia wants the position in the grid relative for the first item returned for a search.
            // So if we're on the second page, the first product position isn't 1, its position is how many places
            // it is away from the first item returned.
            // So if we're not on the first page, send algolia the page index (zero based) and multiply it by
            // the ammount of items on the page (products per page).
            itemLink.addEventListener('click', (e) => {
                if (objectID) {
                    sendAlgoliaClickEvent({
                        objectID,
                        position,
                    });
                }
            });
        });
    };
    listenForDetailPageClicks();
}

const itemidIndexMapPrefix = 'itemid_query';


export function purchasedObjectIDsAfterSearch({ items = [], totalPrice = 0 })
{
    const trackedItems = items
        .map((item) => ({ item, ...getItemidQueryAndIndex(item.itemid) }))
        .filter(({ queryID }) => queryID && isValidAlgoliaQueryID(queryID));

    if (trackedItems.length === 0) {
        return;
    }

    initialize();

    let firstIndex;
    const objectData = trackedItems.map(({ item, queryID, index }) => {
        if (index) {
            firstIndex = index;
        }
        return {
            queryID,
            price: item.price ? parseFloat(item.price) : 0,
            discount: item.discount ? parseFloat(item.discount) : 0,
            quantity: item.quantity ? parseInt(item.quantity) : 0,
        };
    });
    const objectIDs = trackedItems.map(({ item }) => item.itemid);
    const params = {
        eventName: 'Order Complete',
        index: firstIndex,
        objectIDs,
        objectData,
        value: parseFloat(totalPrice),
        currency: 'USD',
    };
    const userToken = getAlgoliaUserToken();
    if (userToken) {
        params.userToken = userToken;
    }

    aa('purchasedObjectIDsAfterSearch', params);
}

/**
 * 
 * @param {string} itemId - The itemID to get the queryID and index for.
 * @returns {Object} { queryID: string, index: string }
 * @description Gets the queryID and index for a given itemID from session storage.
 */
export function getItemidQueryAndIndex(itemId)
{   
    const stored = sessionStorage.getItem(`${itemidIndexMapPrefix}:${itemId.toLowerCase()}`);
    if (stored) {
        const json = JSON.parse(stored);
        if (json && json.queryID) {
            return { queryID: json.queryID, index: json.index || getDefaultIndex() };
        }
    }
    return { queryID: null, index: null };
}

export function sendAlgoliaAddToCartEvent({ objectID, price, quantity }) {
    const { queryID, index } = getLatestAlgoliaQueryData();

    if (queryID && index) {
        sessionStorage.setItem(`${itemidIndexMapPrefix}:${objectID.toLowerCase()}`, JSON.stringify({ queryID, index }));
    }

    const dedupeKey = 'last_stored_conversion';
    if(sessionStorage.getItem(dedupeKey) === objectID) {
        return;
    }

    sessionStorage.setItem(dedupeKey, objectID);
    let eventMethod = 'addedToCartObjectIDsAfterSearch';
    if (!(queryID && index)) {
        eventMethod = 'addedToCartObjectIDs';
    }

    if (!queryID || !isValidAlgoliaQueryID(queryID)) {
        return;
    }

    initialize();

    const payload = {
        eventName: 'Added to Cart',
        index,
        objectIDs: [objectID],
        queryID,
        currency: 'USD',
        value: price ? parseFloat(price) : undefined,
    };
    const userToken = getAlgoliaUserToken();
    if (userToken) {
        payload.userToken = userToken;
    }

    if (price && quantity) {
        payload.objectData = [{
            objectID: objectID,
            price: parseFloat(price),
            quantity: parseInt(quantity),
        }];
    }

    aa(eventMethod, payload);;
}

export function sendAlgoliaConversionEvent({ objectID, price, quantity }) {
    console.warn('sendAlgoliaConversionEvent is deprecated. Use sendAlgoliaAddToCartEvent instead.');
    sendAlgoliaAddToCartEvent({ objectID, price, quantity });
}

const search_key = 'storefront_search';
const MAX_STOREFRONT_SEARCH_ITEMS = 10;
function getSessionStorageOrDefault(defaultValue = null) {
    let result = JSON.parse(sessionStorage.getItem(search_key)) ?? defaultValue;

    if (typeof result != typeof defaultValue && defaultValue != null) {
        return defaultValue;
    }

    return result;
}

// Algolia requires the index reported on an event to actually contain the reported objectID.
// These grids only ever serve objects from the default products index or one of its sort
// replicas, so a queryID captured against an unrelated index -- a Gear Exchange search, say --
// must not be attributed to a click here even when it was the most recent search in the session.
function servesCurrentGrid(index) {
    const base = getDefaultIndex();
    return index === base || index.startsWith(`${base}_`);
}

// Entries predating a populated _index stored no index at all; back then the stack was
// single-index, so they always meant the default products index.
function resolveEntryIndex(index) {
    return typeof index === 'string' && index.trim() ? index.trim() : getDefaultIndex();
}

export function getLatestAlgoliaQueryData()
{
    const { queryID: urlQueryID, index: urlIndex } = getQueryDataFromUrl();
    if (urlQueryID && servesCurrentGrid(resolveEntryIndex(urlIndex))) {
        return { queryID: urlQueryID, index: resolveEntryIndex(urlIndex) };
    }

    // Newest-first: the most recent search whose index can actually contain this grid's objects
    // wins, rather than whichever search merely happened last.
    const storageValue = getSessionStorageOrDefault([]);
    for (let i = storageValue.length - 1; i >= 0; i -= 1) {
        const { queryID, index } = storageValue[i] || {};
        const resolvedIndex = resolveEntryIndex(index);
        if (queryID && servesCurrentGrid(resolvedIndex)) {
            return { queryID, index: resolvedIndex };
        }
    }

    // No compatible search in this session: report no queryID so callers emit the plain,
    // unattributed event instead of stamping this objectID against a foreign index.
    return { queryID: undefined, index: getDefaultIndex() };
}

export function setStorefrontSearchCookie(queryID, indexName) {
    const cookieData = {
        queryID,
        index: indexName
    };
    let storageValue = getSessionStorageOrDefault([]);
    storageValue.push(cookieData);
    if (storageValue.length > MAX_STOREFRONT_SEARCH_ITEMS) {
        storageValue = storageValue.slice(-MAX_STOREFRONT_SEARCH_ITEMS);
    }
    sessionStorage.setItem(search_key, JSON.stringify(storageValue));
}

setStorefrontSearchCookie.fromStorefrontQueryParameters = () => {
    const { queryID, index } = getQueryDataFromUrl();
    if (queryID) {
        setStorefrontSearchCookie(queryID, index);
    }
}